import React, {
  ReactNode,
  useState,
  useEffect,
  useCallback,
  ComponentType,
} from "react";
import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
  fallbackComponent?: ComponentType<{
    error: Error;
    resetError: () => void;
  }>;
  onError?: (error: Error, errorInfo: { componentStack: string }) => void;
  resetKeys?: any[];
}

interface ErrorState {
  error: Error | null;
}

const isProduction = process.env.NODE_ENV === "production";

export const AppErrorBoundary: React.FC<Props> = ({
  children,
  fallbackComponent: FallbackComponent,
  onError,
  resetKeys = [],
}) => {
  const [errorState, setErrorState] = useState<ErrorState>({ error: null });
  const [prevResetKeys, setPrevResetKeys] = useState<any[]>(resetKeys);

  const resetError = useCallback(() => {
    setErrorState({ error: null });
  }, []);

  useEffect(() => {
    const changed =
      prevResetKeys.length !== resetKeys.length ||
      prevResetKeys.some((key, i) => key !== resetKeys[i]);

    if (changed) {
      resetError();
      setPrevResetKeys(resetKeys);
    }
  }, [resetKeys, prevResetKeys, resetError]);

  if (errorState.error) {
    if (FallbackComponent) {
      return (
        <FallbackComponent error={errorState.error} resetError={resetError} />
      );
    }

    return (
      <div style={{ padding: 24, fontFamily: "sans-serif", color: "#333" }}>
        <h1>Something went wrong</h1>
        <p>
          The application encountered an unexpected error. Please try again.
        </p>
        {!isProduction && (
          <pre style={{ whiteSpace: "pre-wrap", color: "red" }}>
            {errorState.error.toString()}
          </pre>
        )}
        <button onClick={resetError} style={{ marginRight: 8 }}>
          Try Again
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{ marginLeft: 8 }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  try {
    return (
      <ErrorCatcher
        onError={(err, info) => {
          setErrorState({ error: err });
          if (onError) onError(err, info);
        }}
      >
        {children}
      </ErrorCatcher>
    );
  } catch (err: any) {
    setErrorState({ error: err });
    return null;
  }
};

class ErrorCatcher extends React.Component<{
  children: ReactNode;
  onError: (error: Error, info: { componentStack: string }) => void;
}> {
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: {
        componentStack: info.componentStack,
      },
    });

    this.props.onError(error, {
      componentStack: info.componentStack || "Unknown",
    });
  }

  render() {
    return this.props.children;
  }
}

export default AppErrorBoundary;
